/**
 * Browse Navigation v2 Prototype
 *
 * Three sidebar modes (icon bar toggles between them):
 *   Browse    — hierarchical nav: Home, Workspaces (with extensions), Tools
 *   Search    — full-panel search with scope filter chips + results
 *   Workspaces — manage which workspaces are active (enable/disable)
 *
 * Basecamp sections: Bookmarks, My Stuff, Todos, Projects, Triage
 * Documents: Finder (default) or Tree (opt-in via right-click)
 *
 * Access: http://flownotes.localhost/?test=browse-nav
 */

import { useState, useRef, useEffect } from 'react'
import {
  Home,
  Building2,
  User,
  ChevronRight,
  ChevronDown,
  ChevronLeft,
  FolderOpen,
  Folder,
  FileText,
  Link2,
  LayoutGrid,
  Settings,
  CheckSquare,
  Boxes,
  Search,
  Bell,
  Trash2,
  Tag,
  Network,
  Plus,
  MoreHorizontal,
  Circle,
  Plug,
  Briefcase,
  GitBranch,
  ArrowLeft,
  FolderTree,
  Layers,
  Bookmark,
  User2,
  ListChecks,
  CalendarClock,
  ToggleLeft,
  ToggleRight,
  X,
  AlignJustify,
  Inbox,
  Mic,
  Maximize2,
} from 'lucide-react'
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels'
import { cn } from '@/lib/utils'
import { BasecampIcon } from '@/components/icons/basecamp-icon'

// ─── Types & mock data ────────────────────────────────────────────────────────

type SidebarMode = 'browse' | 'search'

type ExtensionChild = { id: string; label: string; icon: React.ElementType; badge?: number }

type ExtensionNode = {
  id: string
  label: string
  icon: React.ElementType
  badge?: number
  children?: ExtensionChild[]
}

type WorkspaceDef = {
  id: string
  name: string
  icon: React.ElementType
  color: string
  type: 'work' | 'personal' | 'client'
  extensions: ExtensionNode[]
  fileCount: number
}

const WORKSPACES: WorkspaceDef[] = [
  {
    id: 'hop-studios',
    name: 'Hop Studios',
    icon: Building2,
    color: '#6366f1',
    type: 'work',
    fileCount: 124,
    extensions: [
      {
        id: 'basecamp',
        label: 'Basecamp',
        icon: BasecampIcon,
        badge: 7,
        children: [
          { id: 'bc-bookmarks', label: 'Bookmarks', icon: Bookmark },
          { id: 'bc-mystuff',   label: 'My Stuff',  icon: User2,      badge: 90 },
          { id: 'bc-todos',     label: 'Todos',     icon: CheckSquare, badge: 7 },
          { id: 'bc-projects',  label: 'Projects',  icon: Boxes },
          { id: 'bc-triage',    label: 'Triage',    icon: CalendarClock },
        ],
      },
      {
        id: 'product-board',
        label: 'Product Board',
        icon: LayoutGrid,
        children: [
          { id: 'pb-now',   label: 'Now',   icon: Circle },
          { id: 'pb-next',  label: 'Next',  icon: Circle },
          { id: 'pb-later', label: 'Later', icon: Circle },
        ],
      },
    ],
  },
  {
    id: 'personal',
    name: 'Personal',
    icon: User,
    color: '#10b981',
    type: 'personal',
    fileCount: 38,
    extensions: [],
  },
  {
    id: 'gschool',
    name: 'G-School',
    icon: Briefcase,
    color: '#f59e0b',
    type: 'client',
    fileCount: 19,
    extensions: [
      {
        id: 'basecamp',
        label: 'Basecamp',
        icon: BasecampIcon,
        badge: 2,
        children: [
          { id: 'bc-bookmarks', label: 'Bookmarks', icon: Bookmark },
          { id: 'bc-mystuff',   label: 'My Stuff',  icon: User2 },
          { id: 'bc-todos',     label: 'Todos',     icon: CheckSquare, badge: 2 },
          { id: 'bc-projects',  label: 'Projects',  icon: Boxes },
          { id: 'bc-triage',    label: 'Triage',    icon: CalendarClock },
        ],
      },
    ],
  },
]

type NavItem =
  | { kind: 'home' }
  | { kind: 'inbox' }
  | { kind: 'files'; workspaceId: string }
  | { kind: 'extension'; workspaceId: string; extensionId: string }
  | { kind: 'extension-child'; workspaceId: string; extensionId: string; childId: string }
  | { kind: 'global'; id: 'notifications' | 'tags' | 'graph' | 'trash' }

/** State for the lower resizable panel — null means no lower pane */
type LowerPane = { kind: 'docs' | 'basecamp'; workspaceId: string } | null

function navKey(item: NavItem): string {
  switch (item.kind) {
    case 'home':             return 'home'
    case 'inbox':            return 'inbox'
    case 'files':            return `files-${item.workspaceId}`
    case 'extension':        return `ext-${item.workspaceId}-${item.extensionId}`
    case 'extension-child':  return `extc-${item.workspaceId}-${item.extensionId}-${item.childId}`
    case 'global':           return `global-${item.id}`
  }
}

// ─── NavRow ───────────────────────────────────────────────────────────────────

interface NavRowProps {
  icon: React.ElementType
  label: string
  indent?: number
  active?: boolean
  badge?: number
  /** Icon tint / active accent color — workspace color */
  color?: string
  chevron?: 'right' | 'down' | null
  muted?: boolean
  onChevronClick?: () => void
  onClick?: () => void
  actions?: React.ReactNode
}

function NavRow({ icon: Icon, label, indent = 0, active = false, badge, color, chevron = null, muted = false, onChevronClick, onClick, actions }: NavRowProps) {
  // Left border: use workspace color when active+color provided, else primary, else transparent
  const borderColor = active ? (color ?? 'hsl(var(--primary))') : 'transparent'
  return (
    <div
      className={cn(
        'group flex items-center gap-1.5 pr-2 py-1 cursor-pointer select-none text-sm transition-colors border-l-2',
        active
          ? 'bg-sidebar-accent text-sidebar-foreground font-medium'
          : 'text-sidebar-foreground/70 hover:bg-sidebar-accent/60 hover:text-sidebar-foreground',
        muted && 'opacity-50',
      )}
      style={{
        paddingLeft: `${6 + indent * 14}px`,  // 6px base + 2px from border = effective 8px
        borderLeftColor: borderColor,
      }}
      onClick={onClick}
    >
      {chevron !== null ? (
        <button className="flex-none w-4 h-4 flex items-center justify-center rounded hover:bg-sidebar-accent -ml-1" onClick={(e) => { e.stopPropagation(); onChevronClick?.() }}>
          {chevron === 'down' ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
        </button>
      ) : <span className="flex-none w-4" />}
      <Icon className="flex-none w-4 h-4" style={color ? { color } : undefined} />
      <span className="flex-1 truncate leading-snug">{label}</span>
      {badge !== undefined && badge > 0 && (
        <span className="flex-none text-[10px] font-medium bg-primary/15 text-primary rounded-full px-1.5 py-0.5 leading-none">
          {badge > 99 ? '99+' : badge}
        </span>
      )}
      {actions && (
        <span className="flex-none opacity-0 group-hover:opacity-100 transition-opacity" onClick={e => e.stopPropagation()}>{actions}</span>
      )}
    </div>
  )
}

// ─── Floating context menu ────────────────────────────────────────────────────

type CtxItem = { label: string; icon?: React.ElementType; onClick: () => void; separator?: boolean }

function FloatingContextMenu({ x, y, items, onClose }: { x: number; y: number; items: CtxItem[]; onClose: () => void }) {
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const h = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) onClose() }
    document.addEventListener('mousedown', h)
    return () => document.removeEventListener('mousedown', h)
  }, [onClose])

  return (
    <div ref={ref} className="fixed z-50 min-w-44 rounded-lg border border-border bg-popover shadow-lg py-1 text-sm" style={{ left: x, top: y }}>
      {items.map((item, i) =>
        item.separator
          ? <div key={i} className="my-1 border-t border-border" />
          : (
            <button key={i} className="w-full flex items-center gap-2 px-3 py-1.5 hover:bg-sidebar-accent text-left transition-colors" onClick={() => { item.onClick(); onClose() }}>
              {item.icon && <item.icon className="w-3.5 h-3.5 text-muted-foreground" />}
              {item.label}
            </button>
          )
      )}
    </div>
  )
}

// ─── Mock folder tree data ────────────────────────────────────────────────────

type DocType = 'work-spec' | 'note' | 'voice-memo' | 'todo-folder'

type FolderNode = {
  id: string
  name: string
  type: 'folder' | 'file'
  count?: number
  docType?: DocType
  excerpt?: string
  author?: string
  modified?: string
  tags?: string[]
  children?: FolderNode[]
}

const MOCK_TREE: Record<string, FolderNode[]> = {
  'hop-studios': [
    { id: 'clients', name: 'clients', type: 'folder', count: 59, modified: '1h ago', children: [
      { id: 'greater-good', name: 'greater-good', type: 'folder', count: 3, modified: '2h ago', children: [
        { id: 'ggsc', name: 'ggsc', type: 'folder', count: 4, modified: '2h ago', children: [
          { id: 'todos-ggsc', name: 'todos', type: 'folder', count: 12, modified: '2h ago', children: [
            { id: 'ggsc-todo-1', name: '9669131017-restructure-events-page', type: 'folder', count: 2, modified: '2h ago' },
            { id: 'ggsc-todo-2', name: '9598560343-site-migration-wix', type: 'folder', count: 1, modified: '3d ago' },
            { id: 'ggsc-todo-3', name: '8819316965-practice-catalog-update', type: 'folder', count: 3, modified: '4d ago' },
          ]},
          { id: 'ggsc-md', name: 'GGSC.md', type: 'file', docType: 'note', excerpt: 'Greater Good Science Center client overview. Primary contacts: Lauren Klein (marketing), IT team via ticket system. Annual retainer contract covers ongoing development and content work.', author: 'Rowan', modified: '3 days ago', tags: ['client', 'overview'] },
        ]},
        { id: 'greatergood', name: 'greatergood', type: 'folder', count: 4, modified: '4h ago', children: [
          { id: 'gg-todos', name: 'todos', type: 'folder', count: 8, modified: '4h ago' },
          { id: 'gg-md', name: 'GreaterGood.md', type: 'file', docType: 'note', excerpt: 'GreaterGood.org main site. WordPress multisite with complex donation flows and ScalePost CDN integration. Performance and tracking are ongoing focus areas.', author: 'Rowan', modified: '1 week ago', tags: ['client', 'overview'] },
        ]},
        { id: 'ggia', name: 'ggia', type: 'folder', count: 8, modified: '1d ago', children: [
          { id: 'ggia-todos', name: 'todos', type: 'folder', count: 6, modified: '1d ago' },
          { id: 'ggia-md', name: 'GGIA.md', type: 'file', docType: 'note', excerpt: 'Greater Good in Action portal for stress reduction research. React-based frontend with SMS functionality, slider interaction tracking, and multi-phase study integration.', author: 'Rowan', modified: '2 days ago', tags: ['client', 'overview'] },
        ]},
      ]},
      { id: 'star', name: 'star', type: 'folder', count: 3, modified: '3 days ago' },
      { id: 'ucsf', name: 'ucsf', type: 'folder', count: 5, modified: '1 week ago', children: [
        { id: 'ucsf-todos', name: 'todos', type: 'folder', count: 4, modified: '1 week ago' },
        { id: 'ucsf-md', name: 'UCSF.md', type: 'file', docType: 'note', excerpt: 'UCSF Medical Center Legacy site migration to Wix. Estimate pending approval. Complex content architecture with 400+ pages and faculty directory integration.', author: 'Rowan', modified: '1 week ago', tags: ['client', 'overview'] },
      ]},
      { id: 'nct', name: 'new-client-template', type: 'folder', count: 8, modified: '1 month ago' },
    ]},
    { id: 'internal', name: 'internal', type: 'folder', count: 32, modified: '10m ago', children: [
      { id: 'int-todos', name: 'todos', type: 'folder', count: 70, modified: '10m ago' },
      { id: 'int-archive', name: 'archive', type: 'folder', count: 15, modified: '2 months ago' },
      { id: 'scrum', name: 'HOP-DAILY-SCRUM.md', type: 'file', docType: 'note', excerpt: 'Daily standup notes and blockers. Updated each morning before standup. Format: Yesterday, Today, Blockers. Reference links to active project status docs.', author: 'Rowan', modified: '10m ago', tags: ['internal', 'scrum'] },
      { id: 'int-priorities', name: '2026-02-05-internal-todos-review.md', type: 'file', docType: 'work-spec', excerpt: 'Internal Hop Studios todo review for Q1 2026. Priority items identified in staff meeting. Action items assigned to team members with target dates.', author: 'Rowan', modified: '1 week ago', tags: ['internal', 'review'] },
      { id: 'int-sop', name: 'wordpress-client-setup-sop.md', type: 'file', docType: 'note', excerpt: 'Standard operating procedure for setting up new WordPress client environments. Covers DNS, hosting, staging, and deployment pipeline configuration.', author: 'Rowan', modified: '3 weeks ago', tags: ['internal', 'sop'] },
    ]},
    { id: 'archive', name: 'archive', type: 'folder', count: 47, modified: '2 months ago' },
    { id: 'addons', name: 'addons', type: 'folder', count: 5, modified: '3 weeks ago' },
  ],
  'personal': [
    { id: 'journal', name: 'journal', type: 'folder', count: 12, modified: '1h ago' },
    { id: 'volleyball', name: 'volleyball', type: 'folder', count: 3, modified: '1d ago', children: [
      { id: 'vb-ref', name: '2026-03-10-ref-letter-samara.md', type: 'file', docType: 'note', excerpt: 'Reference letter draft for Samara Brown applying for a volleyball coaching position. Highlights leadership, skill development and player engagement over two seasons.', author: 'Rowan', modified: '1d ago', tags: ['volleyball', 'reference'] },
      { id: 'vb-roster', name: 'roster.md', type: 'file', docType: 'note', excerpt: 'Current team roster with contact info, positions, and availability notes for the spring season.', author: 'Rowan', modified: '3 days ago', tags: ['volleyball'] },
    ]},
    { id: 'dj-rowan', name: 'dj-rowan', type: 'folder', count: 4, modified: '2 weeks ago', children: [
      { id: 'dj-blast', name: 'blast-ya-edit-notes.md', type: 'file', docType: 'note', excerpt: 'Edit notes for Blast Ya track. New ending structure, drum breakdown at bar 64, drop rebuilt with heavier sub. Reference mixes linked in Dropbox.', author: 'Rowan', modified: '2 weeks ago', tags: ['music', 'ableton'] },
      { id: 'dj-voice', name: '2026-02-14-track-ideas.m4a', type: 'file', docType: 'voice-memo', excerpt: 'Ideas for new track — melodic techno direction. References Objekt and Burial. Drum machine textures over long atmospheric pads.', author: 'Rowan', modified: '3 weeks ago', tags: ['music', 'ideas'] },
    ]},
    { id: 'notes', name: 'notes', type: 'folder', count: 8, modified: '1 week ago' },
  ],
  'gschool': [
    { id: 'gs-todos', name: 'todos', type: 'folder', count: 12, modified: '2h ago' },
    { id: 'gs-meetings', name: 'meetings', type: 'folder', count: 4, modified: '1d ago', children: [
      { id: 'gs-meeting-1', name: '2026-03-03-faculty-engagement-todos.md', type: 'file', docType: 'work-spec', excerpt: 'Open todos from faculty engagement session. Key action items: update tool language for accessibility, review student feedback forms, schedule follow-up with curriculum committee by March 15.', author: 'Rowan', modified: '1d ago', tags: ['gschool', 'meetings'] },
      { id: 'gs-meeting-2', name: '2026-02-17-kickoff.md', type: 'file', docType: 'note', excerpt: 'Kickoff meeting notes for G-School spring semester engagement. Attendees, agenda, outcomes, and next steps documented. Basecamp project created.', author: 'Rowan', modified: '3 weeks ago', tags: ['gschool', 'kickoff'] },
    ]},
    { id: 'gs-md', name: 'G-School.md', type: 'file', docType: 'note', excerpt: 'G-School client overview. Main contact Chris Kafer. Engagement scope covers UX review, content strategy, and annual reporting. Monthly retainer through June 2026.', author: 'Rowan', modified: '2 weeks ago', tags: ['client', 'overview'] },
    { id: 'gs-vm', name: '2026-03-01-site-audit-notes.m4a', type: 'file', docType: 'voice-memo', excerpt: 'Voice notes from site audit walkthrough. Navigation issues on mobile, course catalog load times, faculty directory search broken on Safari.', author: 'Rowan', modified: '1 week ago', tags: ['gschool', 'audit'] },
  ],
}

// ─── IDE tree node ────────────────────────────────────────────────────────────

function TreeFileNode({ node, depth = 0, wsColor, selectedId, onSelect, onOpen }: {
  node: FolderNode
  depth?: number
  wsColor?: string
  selectedId?: string | null
  onSelect?: (node: FolderNode) => void
  onOpen?: (node: FolderNode) => void
}) {
  const [open, setOpen] = useState(depth < 1)
  const isSelected = selectedId === node.id
  const typeCfg = node.docType ? DOC_TYPE_CONFIG[node.docType] : null

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    onSelect?.(node)
    if (node.type === 'folder') setOpen(o => !o)
  }

  const handleDoubleClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    onOpen?.(node)
  }

  return (
    <div>
      <div
        className={cn(
          'flex items-center gap-2 py-1 rounded cursor-pointer transition-colors',
          isSelected
            ? 'bg-primary/15 text-foreground'
            : 'hover:bg-muted/60 text-foreground/75 hover:text-foreground'
        )}
        style={{ paddingLeft: `${10 + depth * 16}px`, paddingRight: '10px' }}
        onClick={handleClick}
        onDoubleClick={handleDoubleClick}
      >
        {/* Chevron for folders */}
        {node.type === 'folder' ? (
          <span className="flex-none w-3.5">
            {open
              ? <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />
              : <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />}
          </span>
        ) : (
          <span className="w-3.5 flex-none" />
        )}

        {/* Icon */}
        {node.type === 'folder' ? (
          open
            ? <FolderOpen className="w-4 h-4 flex-none" style={{ color: wsColor ? wsColor + 'cc' : undefined }} />
            : <Folder className="w-4 h-4 flex-none" style={{ color: wsColor ? wsColor + 'cc' : undefined }} />
        ) : node.docType === 'voice-memo' ? (
          <Mic className={cn('w-4 h-4 flex-none', typeCfg?.textClass ?? 'text-muted-foreground')} />
        ) : (
          <FileText className={cn('w-4 h-4 flex-none', typeCfg?.textClass ?? 'text-muted-foreground')} />
        )}

        <span className="flex-1 truncate text-sm">{node.name}</span>

        {node.count !== undefined && node.type === 'folder' && (
          <span className="flex-none text-[10px] text-muted-foreground bg-muted/60 rounded px-1.5 py-0.5">{node.count}</span>
        )}
      </div>
      {node.type === 'folder' && open && node.children?.map(child => (
        <TreeFileNode
          key={child.id}
          node={child}
          depth={depth + 1}
          wsColor={wsColor}
          selectedId={selectedId}
          onSelect={onSelect}
          onOpen={onOpen}
        />
      ))}
    </div>
  )
}

// ─── WorkspaceSection (Browse mode) ──────────────────────────────────────────

interface WorkspaceSectionProps {
  workspace: WorkspaceDef
  expandedWorkspaces: Set<string>
  expandedExtensions: Set<string>
  activeNav: NavItem
  treeMode: Set<string>
  lowerPane: LowerPane
  onToggleWorkspace: (id: string) => void
  onToggleExtension: (wsId: string, extId: string) => void
  onNavigate: (item: NavItem) => void
  onToggleTreeMode: (wsId: string) => void
  onToggleLowerPane: (kind: 'docs' | 'basecamp', wsId: string) => void
}

function WorkspaceSection({ workspace, expandedWorkspaces, expandedExtensions, activeNav, treeMode, lowerPane, onToggleWorkspace, onToggleExtension, onNavigate, onToggleTreeMode, onToggleLowerPane }: WorkspaceSectionProps) {
  const WsIcon = workspace.icon
  const wsExpanded = expandedWorkspaces.has(workspace.id)
  const activeNavKey = navKey(activeNav)
  const extKey = (extId: string) => navKey({ kind: 'extension', workspaceId: workspace.id, extensionId: extId })
  const docsActive = lowerPane?.kind === 'docs' && lowerPane.workspaceId === workspace.id
  const basecampActive = lowerPane?.kind === 'basecamp' && lowerPane.workspaceId === workspace.id

  return (
    <div data-testid={`browse-nav-workspace-${workspace.id}`} className="mb-0.5">
      {/* Workspace header — icon pill + name, full-row highlight when expanded */}
      <div
        className={cn(
          'group flex items-center gap-2 px-2 py-1 cursor-pointer select-none transition-colors border-l-[3px]',
          wsExpanded ? 'text-sidebar-foreground' : 'text-sidebar-foreground/70 hover:text-sidebar-foreground',
        )}
        style={{
          borderLeftColor: wsExpanded ? workspace.color : 'transparent',
          backgroundColor: wsExpanded ? workspace.color + '12' : undefined,
        }}
        onMouseEnter={e => { if (!wsExpanded) (e.currentTarget as HTMLElement).style.backgroundColor = workspace.color + '08' }}
        onMouseLeave={e => { if (!wsExpanded) (e.currentTarget as HTMLElement).style.backgroundColor = '' }}
        onClick={() => onToggleWorkspace(workspace.id)}
      >
        {/* Chevron */}
        <span className="flex-none w-3 h-3 flex items-center justify-center text-muted-foreground">
          {wsExpanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
        </span>
        {/* Colored icon pill — larger for workspace-level prominence */}
        <span
          className="flex-none w-6 h-6 rounded flex items-center justify-center"
          style={{ backgroundColor: workspace.color + '30' }}
        >
          <WsIcon className="w-3.5 h-3.5" style={{ color: workspace.color }} />
        </span>
        <span className="flex-1 truncate text-[13px] font-semibold leading-snug">{workspace.name}</span>
        <span className="flex-none opacity-0 group-hover:opacity-100 transition-opacity">
          <button className="p-0.5 rounded hover:bg-sidebar-accent" onClick={e => e.stopPropagation()}>
            <MoreHorizontal className="w-3 h-3" />
          </button>
        </span>
      </div>

      {/* Workspace children — mode selectors, no inline expansion */}
      {wsExpanded && (
        <div>
          {/* Extensions as mode selectors */}
          {workspace.extensions.map(ext => {
            const ExtIcon = ext.icon
            // Basecamp extension: mode selector → opens lower pane
            const isBasecamp = ext.id === 'basecamp'
            const isActive = isBasecamp ? basecampActive : activeNavKey === extKey(ext.id)
            return (
              <NavRow
                key={ext.id}
                icon={ExtIcon}
                label={ext.label}
                indent={1}
                badge={ext.badge}
                active={isActive}
                color={isActive ? workspace.color : undefined}
                onClick={() => {
                  if (isBasecamp) {
                    onToggleLowerPane('basecamp', workspace.id)
                  } else {
                    onNavigate({ kind: 'extension', workspaceId: workspace.id, extensionId: ext.id })
                  }
                }}
              />
            )
          })}

          {workspace.extensions.length === 0 && (
            <div className="flex items-center gap-1.5 py-1 text-xs text-muted-foreground cursor-pointer hover:text-sidebar-foreground transition-colors" style={{ paddingLeft: `${8 + 14}px` }}>
              <Plug className="w-3 h-3" /><span>Add extension</span>
            </div>
          )}

          {/* Documents — mode selector → opens lower pane */}
          <NavRow
            icon={FolderOpen}
            label="Documents"
            indent={1}
            active={docsActive}
            color={docsActive ? workspace.color : undefined}
            onClick={() => onToggleLowerPane('docs', workspace.id)}
          />
        </div>
      )}
    </div>
  )
}

// ─── Documents Explorer ───────────────────────────────────────────────────────

type DocFilter = 'all' | DocType
type SortKey = 'modified' | 'name'

const DOC_TYPE_CONFIG: Record<DocType, { label: string; textClass: string; badgeClass: string }> = {
  'work-spec':   { label: 'Work Spec',  textClass: 'text-blue-400',   badgeClass: 'bg-blue-500/10 text-blue-400 border-blue-500/20' },
  'note':        { label: 'Note',       textClass: 'text-green-400',  badgeClass: 'bg-green-500/10 text-green-400 border-green-500/20' },
  'voice-memo':  { label: 'Voice Memo', textClass: 'text-purple-400', badgeClass: 'bg-purple-500/10 text-purple-400 border-purple-500/20' },
  'todo-folder': { label: 'Todo',       textClass: 'text-orange-400', badgeClass: 'bg-orange-500/10 text-orange-400 border-orange-500/20' },
}

function FileTypeIcon({ type, className }: { type: DocType; className?: string }) {
  const cfg = DOC_TYPE_CONFIG[type]
  if (type === 'voice-memo') return <Mic className={cn('w-4 h-4', cfg.textClass, className)} />
  return <FileText className={cn('w-4 h-4', cfg.textClass, className)} />
}

// ── List view content ─────────────────────────────────────────────────────────

function ListViewContent({ folders, files, wsColor, selectedId, onSelect, onOpen }: {
  folders: FolderNode[]
  files: FolderNode[]
  wsColor: string
  selectedId: string | null
  onSelect: (node: FolderNode) => void
  onOpen: (node: FolderNode) => void
}) {
  const hasFolders = folders.length > 0
  const hasFiles = files.length > 0
  const empty = !hasFolders && !hasFiles

  return (
    <div>
      {empty && (
        <div className="py-16 text-center">
          <Folder className="w-10 h-10 text-muted-foreground/20 mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">No items here</p>
        </div>
      )}

      {/* Column headers */}
      {(hasFolders || hasFiles) && (
        <div className="sticky top-0 z-10 flex items-center px-4 py-2 border-b border-border bg-background/95 backdrop-blur-sm">
          <span className="flex-1 text-[11px] font-medium text-muted-foreground uppercase tracking-wide">Name</span>
          <span className="w-24 text-right text-[11px] font-medium text-muted-foreground uppercase tracking-wide hidden sm:block">Type</span>
          <span className="w-28 text-right text-[11px] font-medium text-muted-foreground uppercase tracking-wide">Modified</span>
          <span className="w-24 text-right text-[11px] font-medium text-muted-foreground uppercase tracking-wide hidden md:block">Author</span>
        </div>
      )}

      {/* Folders */}
      {hasFolders && (
        <div className="divide-y divide-border/40">
          {folders.map(folder => (
            <div
              key={folder.id}
              onClick={() => onSelect(folder)}
              onDoubleClick={() => onOpen(folder)}
              className={cn(
                'flex items-center px-4 py-2.5 transition-colors text-left cursor-default select-none group',
                selectedId === folder.id
                  ? 'bg-primary/10 border-l-2 border-l-primary'
                  : 'hover:bg-muted/40 border-l-2 border-l-transparent'
              )}
            >
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <FolderOpen className="w-4 h-4 flex-none transition-colors" style={{ color: wsColor + 'bb' }} />
                <span className="text-sm font-medium truncate">{folder.name}</span>
                {folder.count !== undefined && (
                  <span className="text-[10px] text-muted-foreground bg-muted rounded px-1.5 py-0.5 flex-none">{folder.count}</span>
                )}
              </div>
              <span className="w-24 hidden sm:block" />
              <span className="w-28 text-right text-xs text-muted-foreground">{folder.modified ?? '—'}</span>
              <span className="w-24 hidden md:block" />
            </div>
          ))}
        </div>
      )}

      {/* Separator if both present */}
      {hasFolders && hasFiles && <div className="border-t border-border/60 mx-4 my-1" />}

      {/* Files */}
      {hasFiles && (
        <div className="divide-y divide-border/40">
          {files.map(file => {
            const typeCfg = file.docType ? DOC_TYPE_CONFIG[file.docType] : null
            return (
              <div
                key={file.id}
                onClick={() => onSelect(file)}
                onDoubleClick={() => onOpen(file)}
                className={cn(
                  'flex items-start px-4 py-3 transition-colors cursor-default select-none',
                  selectedId === file.id
                    ? 'bg-primary/10 border-l-2 border-l-primary'
                    : 'hover:bg-muted/40 border-l-2 border-l-transparent'
                )}
              >
                <div className="flex items-start gap-3 flex-1 min-w-0">
                  {file.docType ? (
                    <FileTypeIcon type={file.docType} className="mt-0.5 flex-none" />
                  ) : (
                    <FileText className="w-4 h-4 text-muted-foreground mt-0.5 flex-none" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium truncate">{file.name}</span>
                      {typeCfg && (
                        <span className={cn('text-[10px] px-1.5 py-0.5 rounded border flex-none hidden sm:inline', typeCfg.badgeClass)}>
                          {typeCfg.label}
                        </span>
                      )}
                    </div>
                    {file.excerpt && (
                      <p className="text-xs text-muted-foreground line-clamp-1 mt-0.5">{file.excerpt}</p>
                    )}
                  </div>
                </div>
                <span className="w-28 text-right text-xs text-muted-foreground flex-none pt-0.5">{file.modified ?? '—'}</span>
                <span className="w-24 text-right text-xs text-muted-foreground flex-none pt-0.5 hidden md:block">{file.author ?? '—'}</span>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

// ── Grid view content ─────────────────────────────────────────────────────────

function GridViewContent({ folders, files, wsColor, selectedId, onSelect, onOpen }: {
  folders: FolderNode[]
  files: FolderNode[]
  wsColor: string
  selectedId: string | null
  onSelect: (node: FolderNode) => void
  onOpen: (node: FolderNode) => void
}) {
  const empty = folders.length === 0 && files.length === 0

  return (
    <div className="p-4">
      {empty && (
        <div className="py-16 text-center">
          <Folder className="w-10 h-10 text-muted-foreground/20 mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">No items here</p>
        </div>
      )}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
        {folders.map(folder => (
          <div
            key={folder.id}
            onClick={() => onSelect(folder)}
            onDoubleClick={() => onOpen(folder)}
            className={cn(
              'p-4 border rounded-lg bg-card transition-all cursor-default select-none',
              selectedId === folder.id
                ? 'border-primary/60 bg-primary/5 ring-1 ring-primary/20'
                : 'border-border hover:border-primary/40 hover:bg-muted/40'
            )}
          >
            <FolderOpen className="w-8 h-8 mb-2" style={{ color: wsColor + 'bb' }} />
            <div className="text-sm font-medium truncate">{folder.name}</div>
            <div className="text-[10px] text-muted-foreground mt-1 flex items-center gap-1 flex-wrap">
              {folder.count !== undefined && <span>{folder.count} items</span>}
              {folder.modified && <><span>·</span><span>{folder.modified}</span></>}
            </div>
          </div>
        ))}
        {files.map(file => {
          const typeCfg = file.docType ? DOC_TYPE_CONFIG[file.docType] : null
          return (
            <div
              key={file.id}
              onClick={() => onSelect(file)}
              onDoubleClick={() => onOpen(file)}
              className={cn(
                'p-4 border rounded-lg bg-card transition-all cursor-default select-none',
                selectedId === file.id
                  ? 'border-primary/60 bg-primary/5 ring-1 ring-primary/20'
                  : 'border-border hover:border-primary/40 hover:bg-muted/40'
              )}
            >
              {file.docType ? (
                <FileTypeIcon type={file.docType} className="w-8 h-8 mb-2" />
              ) : (
                <FileText className="w-8 h-8 text-muted-foreground mb-2" />
              )}
              <div className="text-sm font-medium truncate mb-1">{file.name}</div>
              {file.excerpt && (
                <p className="text-[11px] text-muted-foreground line-clamp-2">{file.excerpt}</p>
              )}
              <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                {typeCfg && (
                  <span className={cn('text-[10px] px-1.5 py-0.5 rounded border', typeCfg.badgeClass)}>
                    {typeCfg.label}
                  </span>
                )}
                {file.modified && <span className="text-[10px] text-muted-foreground">{file.modified}</span>}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ── Explorer Preview Pane ─────────────────────────────────────────────────────
// Matches the SecondaryPanelContent layout used in the production app

function ExplorerPreview({ node, wsColor, wsName, wsIcon: WsIcon, onOpen, onClose }: {
  node: FolderNode
  wsColor: string
  wsName: string
  wsIcon: React.ElementType
  onOpen: (node: FolderNode) => void
  onClose: () => void
}) {
  const typeCfg = node.docType ? DOC_TYPE_CONFIG[node.docType] : null

  return (
    <div className="h-full flex flex-col bg-card border-l border-border overflow-hidden">
      {/* Header — matches SecondaryPanelContent exactly */}
      <div className="px-4 py-3 border-b border-border bg-card flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-2 flex-1 min-w-0 overflow-hidden">
          {/* Breadcrumb: workspace > filename */}
          <button className="text-xs font-semibold uppercase tracking-wider text-muted-foreground hover:text-foreground transition-colors flex-none flex items-center gap-1.5">
            <WsIcon className="w-3 h-3" style={{ color: wsColor }} />
            {wsName}
          </button>
          <ChevronRight className="h-3 w-3 text-muted-foreground flex-shrink-0" />
          <span className="text-xs font-medium text-foreground truncate">{node.name}</span>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          {/* Open button */}
          <button
            onClick={() => onOpen(node)}
            title={node.type === 'folder' ? 'Open Folder' : 'Open in editor'}
            className="h-7 w-7 flex items-center justify-center rounded hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
          >
            <Maximize2 className="h-4 w-4" />
          </button>
          {/* Close button */}
          <button
            onClick={onClose}
            title="Close"
            className="h-7 w-7 flex items-center justify-center rounded hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-5">
        {/* Type + icon header */}
        <div>
          <div className="flex items-center gap-3 mb-3">
            {node.type === 'folder' ? (
              <FolderOpen className="w-8 h-8 flex-none" style={{ color: wsColor + 'cc' }} />
            ) : node.docType === 'voice-memo' ? (
              <Mic className={cn('w-8 h-8 flex-none', typeCfg?.textClass)} />
            ) : (
              <FileText className={cn('w-8 h-8 flex-none', typeCfg?.textClass ?? 'text-muted-foreground')} />
            )}
            <div className="min-w-0">
              <p className="text-sm font-semibold leading-tight break-all">{node.name}</p>
              {typeCfg && (
                <span className={cn('inline-block mt-1 text-[10px] px-1.5 py-0.5 rounded border', typeCfg.badgeClass)}>
                  {typeCfg.label}
                </span>
              )}
              {node.type === 'folder' && (
                <span className="inline-block mt-1 text-[10px] px-1.5 py-0.5 rounded border border-border bg-muted text-muted-foreground">
                  Folder{node.count !== undefined ? ` · ${node.count} items` : ''}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Excerpt */}
        {node.excerpt && (
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">Preview</p>
            <p className="text-xs text-foreground/80 leading-relaxed">{node.excerpt}</p>
          </div>
        )}

        {/* Metadata */}
        <div className="space-y-2 text-xs">
          {node.modified && (
            <div className="flex justify-between gap-2">
              <span className="text-muted-foreground">Modified</span>
              <span className="text-foreground font-medium">{node.modified}</span>
            </div>
          )}
          {node.author && (
            <div className="flex justify-between gap-2">
              <span className="text-muted-foreground">Author</span>
              <span className="text-foreground font-medium">{node.author}</span>
            </div>
          )}
        </div>

        {/* Tags */}
        {node.tags && node.tags.length > 0 && (
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">Tags</p>
            <div className="flex flex-wrap gap-1.5">
              {node.tags.map(tag => (
                <span key={tag} className="text-[10px] px-2 py-0.5 rounded-full bg-muted border border-border text-muted-foreground">{tag}</span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// ── DocumentsExplorer (main component) ───────────────────────────────────────

interface DocumentsExplorerProps {
  workspace: WorkspaceDef
  treeMode: boolean
  onToggleTreeMode: () => void
}

function DocumentsExplorer({ workspace, treeMode, onToggleTreeMode }: DocumentsExplorerProps) {
  const [history, setHistory] = useState<string[][]>([[]])
  const [historyIdx, setHistoryIdx] = useState(0)
  const [filter, setFilter] = useState<DocFilter>('all')
  const [sort, setSort] = useState<SortKey>('modified')
  const [viewMode, setViewMode] = useState<'list' | 'grid'>('list')
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [selectedNode, setSelectedNode] = useState<FolderNode | null>(null)
  const contentRef = useRef<HTMLDivElement>(null)

  const currentPath = history[historyIdx]

  const navigateTo = (newPath: string[]) => {
    const trimmed = history.slice(0, historyIdx + 1)
    setHistory([...trimmed, newPath])
    setHistoryIdx(trimmed.length)
    setFilter('all')
    setSelectedId(null)
    setSelectedNode(null)
  }

  const goBack = () => { if (historyIdx > 0) setHistoryIdx(i => i - 1) }
  const goForward = () => { if (historyIdx < history.length - 1) setHistoryIdx(i => i + 1) }

  const canBack = historyIdx > 0
  const canForward = historyIdx < history.length - 1

  // Resolve current folder contents
  const getCurrentItems = (): FolderNode[] => {
    const tree = MOCK_TREE[workspace.id] ?? []
    if (currentPath.length === 0) return tree
    let nodes = tree
    for (const seg of currentPath) {
      const found = nodes.find(n => n.name === seg)
      if (!found || !found.children) return []
      nodes = found.children
    }
    return nodes
  }

  const allItems = getCurrentItems()
  const folders = allItems.filter(n => n.type === 'folder')
  const files = allItems.filter(n => n.type === 'file')

  // Derive available types from files in current view (smart chips)
  const availableTypes = [...new Set(files.map(f => f.docType).filter(Boolean))] as DocType[]

  // Apply filter + sort
  const filteredFiles = filter === 'all' ? files : files.filter(f => f.docType === filter)
  const sortedFolders = sort === 'name' ? [...folders].sort((a, b) => a.name.localeCompare(b.name)) : folders
  const sortedFiles = sort === 'name' ? [...filteredFiles].sort((a, b) => a.name.localeCompare(b.name)) : filteredFiles

  // Flat ordered list for keyboard navigation
  const orderedItems = [...sortedFolders, ...sortedFiles]

  const handleSelect = (node: FolderNode) => {
    setSelectedId(node.id)
    setSelectedNode(node)
  }

  const handleOpen = (node: FolderNode) => {
    if (node.type === 'folder') {
      navigateTo([...currentPath, node.name])
    } else {
      // In production: open document in tab
      alert(`Open document: ${node.name}`)
    }
  }

  const handleDeselect = () => {
    setSelectedId(null)
    setSelectedNode(null)
  }

  // Keyboard navigation
  useEffect(() => {
    const el = contentRef.current
    if (!el) return
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleDeselect()
        return
      }
      if (e.key === 'Enter' && selectedNode) {
        handleOpen(selectedNode)
        return
      }
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault()
        if (orderedItems.length === 0) return
        const currentIdx = selectedId ? orderedItems.findIndex(n => n.id === selectedId) : -1
        const nextIdx = e.key === 'ArrowDown'
          ? (currentIdx < orderedItems.length - 1 ? currentIdx + 1 : 0)
          : (currentIdx > 0 ? currentIdx - 1 : orderedItems.length - 1)
        const next = orderedItems[nextIdx]
        handleSelect(next)
        // Scroll selected row into view
        const row = el.querySelector(`[data-item-id="${next.id}"]`)
        row?.scrollIntoView({ block: 'nearest' })
      }
    }
    el.addEventListener('keydown', handleKey)
    return () => el.removeEventListener('keydown', handleKey)
  }, [selectedId, selectedNode, orderedItems])

  // Clear selection when folder changes
  useEffect(() => {
    setSelectedId(null)
    setSelectedNode(null)
  }, [historyIdx])

  // Breadcrumb segments
  const crumbs = [
    { label: workspace.name, path: [] as string[], isLast: currentPath.length === 0 },
    ...currentPath.map((seg, i) => ({
      label: seg,
      path: currentPath.slice(0, i + 1),
      isLast: i === currentPath.length - 1,
    })),
  ]

  const WsIcon = workspace.icon

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* ── Header: nav + breadcrumb ── */}
      <div className="flex-none border-b border-border">
        <div className="px-4 pt-4 pb-2 flex items-center gap-1.5">
          {/* Back / Forward */}
          <button
            onClick={goBack}
            disabled={!canBack}
            title="Back"
            className={cn('w-7 h-7 flex items-center justify-center rounded transition-colors flex-none',
              canBack ? 'hover:bg-muted text-foreground' : 'text-muted-foreground/25 cursor-default')}
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button
            onClick={goForward}
            disabled={!canForward}
            title="Forward"
            className={cn('w-7 h-7 flex items-center justify-center rounded transition-colors flex-none',
              canForward ? 'hover:bg-muted text-foreground' : 'text-muted-foreground/25 cursor-default')}
          >
            <ChevronRight className="w-4 h-4" />
          </button>

          {/* Breadcrumb */}
          <div className="flex items-center gap-1 flex-1 min-w-0 overflow-hidden ml-1">
            <WsIcon className="w-4 h-4 flex-none" style={{ color: workspace.color }} />
            {crumbs.map((crumb, i) => (
              <span key={i} className="flex items-center gap-1 min-w-0 overflow-hidden">
                {i > 0 && <ChevronRight className="w-3 h-3 text-muted-foreground/50 flex-none" />}
                {crumb.isLast ? (
                  <span className="text-sm font-semibold truncate">{crumb.label}</span>
                ) : (
                  <button
                    onClick={() => navigateTo(crumb.path)}
                    className="text-sm text-muted-foreground hover:text-foreground truncate transition-colors"
                  >
                    {crumb.label}
                  </button>
                )}
              </span>
            ))}
          </div>

          {/* Tree view toggle */}
          <button
            onClick={onToggleTreeMode}
            title={treeMode ? 'Switch to Folder view' : 'Switch to Tree view'}
            className={cn('flex items-center gap-1.5 px-2 py-1 rounded text-xs border transition-colors flex-none',
              treeMode ? 'bg-primary/10 text-primary border-primary/20' : 'bg-muted text-muted-foreground border-border hover:text-foreground')}
          >
            {treeMode ? <GitBranch className="w-3 h-3" /> : <Folder className="w-3 h-3" />}
            {treeMode ? 'Tree' : 'Folders'}
          </button>
        </div>

        {/* Filter + Sort row (hidden in tree mode) */}
        {!treeMode && (
          <div className="px-4 pb-3 flex items-center gap-2">
            {/* Smart type chips */}
            <div className="flex items-center gap-1 flex-1 flex-wrap">
              <button
                onClick={() => setFilter('all')}
                className={cn(
                  'px-2.5 py-1 rounded-full text-xs border transition-colors',
                  filter === 'all'
                    ? 'text-white border-transparent'
                    : 'bg-muted text-muted-foreground border-border hover:border-primary/40 hover:text-foreground'
                )}
                style={filter === 'all' ? { backgroundColor: workspace.color } : undefined}
              >
                All
              </button>
              {availableTypes.map(type => (
                <button
                  key={type}
                  onClick={() => setFilter(filter === type ? 'all' : type)}
                  className={cn(
                    'px-2.5 py-1 rounded-full text-xs border transition-colors',
                    filter === type
                      ? 'text-white border-transparent'
                      : 'bg-muted text-muted-foreground border-border hover:border-primary/40 hover:text-foreground'
                  )}
                  style={filter === type ? { backgroundColor: workspace.color } : undefined}
                >
                  {DOC_TYPE_CONFIG[type].label}s
                </button>
              ))}
              {files.length > 0 && filter !== 'all' && (
                <span className="text-xs text-muted-foreground ml-1">
                  {filteredFiles.length} of {files.length}
                </span>
              )}
            </div>

            {/* Sort select */}
            <select
              value={sort}
              onChange={e => setSort(e.target.value as SortKey)}
              className="text-xs bg-muted border border-border rounded px-2 py-1 text-muted-foreground cursor-pointer outline-none"
            >
              <option value="modified">Most Recent</option>
              <option value="name">Name A–Z</option>
            </select>

            {/* View toggle */}
            <div className="flex border border-border rounded overflow-hidden flex-none">
              <button
                onClick={() => setViewMode('list')}
                title="List view"
                className={cn('px-2 py-1 transition-colors',
                  viewMode === 'list' ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:text-foreground')}
              >
                <AlignJustify className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={() => setViewMode('grid')}
                title="Grid view"
                className={cn('px-2 py-1 transition-colors',
                  viewMode === 'grid' ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:text-foreground')}
              >
                <LayoutGrid className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── Content + Preview side-by-side ── */}
      <div className="flex flex-1 overflow-hidden">
        {/* Main content — keyboard-navigable container */}
        <div
          ref={contentRef}
          tabIndex={0}
          className="flex-1 overflow-y-auto outline-none"
          onClick={e => {
            // Click on empty space = deselect
            if (e.target === e.currentTarget) handleDeselect()
          }}
        >
          {treeMode ? (
            <div className="py-2">
              <div className="flex items-center gap-1.5 px-3 py-1.5 mb-1 text-xs text-muted-foreground border-b border-border/50">
                <GitBranch className="w-3 h-3" />
                <span>Tree view — right-click "Documents" in sidebar to switch back</span>
              </div>
              {(MOCK_TREE[workspace.id] ?? []).map(node => (
                <TreeFileNode
                  key={node.id}
                  node={node}
                  wsColor={workspace.color}
                  selectedId={selectedId}
                  onSelect={handleSelect}
                  onOpen={handleOpen}
                />
              ))}
            </div>
          ) : viewMode === 'list' ? (
            <div onClick={e => { if (e.target === e.currentTarget) handleDeselect() }}>
              {/* Wrap items with data-item-id for scroll-into-view */}
              <ListViewWithIds
                folders={sortedFolders}
                files={sortedFiles}
                wsColor={workspace.color}
                selectedId={selectedId}
                onSelect={handleSelect}
                onOpen={handleOpen}
              />
            </div>
          ) : (
            <GridViewContent
              folders={sortedFolders}
              files={sortedFiles}
              wsColor={workspace.color}
              selectedId={selectedId}
              onSelect={handleSelect}
              onOpen={handleOpen}
            />
          )}
        </div>

        {/* Preview pane — slides in when something is selected */}
        {selectedNode && (
          <div className="w-72 xl:w-80 flex-none">
            <ExplorerPreview
              node={selectedNode}
              wsColor={workspace.color}
              wsName={workspace.name}
              wsIcon={WsIcon}
              onOpen={handleOpen}
              onClose={handleDeselect}
            />
          </div>
        )}
      </div>

      {/* Keyboard hint */}
      {!treeMode && orderedItems.length > 0 && !selectedNode && (
        <div className="flex-none px-4 py-1.5 border-t border-border/50 text-[10px] text-muted-foreground/50 flex items-center gap-3">
          <span>↑↓ navigate</span>
          <span>↵ open</span>
          <span>click to select</span>
          <span>double-click to open</span>
        </div>
      )}
    </div>
  )
}

// Wrapper that adds data-item-id attributes for keyboard scroll-into-view
function ListViewWithIds({ folders, files, wsColor, selectedId, onSelect, onOpen }: {
  folders: FolderNode[]
  files: FolderNode[]
  wsColor: string
  selectedId: string | null
  onSelect: (node: FolderNode) => void
  onOpen: (node: FolderNode) => void
}) {
  const hasFolders = folders.length > 0
  const hasFiles = files.length > 0
  const empty = !hasFolders && !hasFiles

  return (
    <div>
      {empty && (
        <div className="py-16 text-center">
          <Folder className="w-10 h-10 text-muted-foreground/20 mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">No items here</p>
        </div>
      )}

      {(hasFolders || hasFiles) && (
        <div className="sticky top-0 z-10 flex items-center px-4 py-2 border-b border-border bg-background/95 backdrop-blur-sm">
          <span className="flex-1 text-[11px] font-medium text-muted-foreground uppercase tracking-wide">Name</span>
          <span className="w-24 text-right text-[11px] font-medium text-muted-foreground uppercase tracking-wide hidden sm:block">Type</span>
          <span className="w-28 text-right text-[11px] font-medium text-muted-foreground uppercase tracking-wide">Modified</span>
          <span className="w-24 text-right text-[11px] font-medium text-muted-foreground uppercase tracking-wide hidden md:block">Author</span>
        </div>
      )}

      {hasFolders && (
        <div className="divide-y divide-border/40">
          {folders.map(folder => (
            <div
              key={folder.id}
              data-item-id={folder.id}
              onClick={() => onSelect(folder)}
              onDoubleClick={() => onOpen(folder)}
              className={cn(
                'flex items-center px-4 py-2.5 transition-colors cursor-default select-none',
                selectedId === folder.id
                  ? 'bg-primary/10 border-l-2 border-l-primary'
                  : 'hover:bg-muted/40 border-l-2 border-l-transparent'
              )}
            >
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <FolderOpen className="w-4 h-4 flex-none" style={{ color: wsColor + 'bb' }} />
                <span className="text-sm font-medium truncate">{folder.name}</span>
                {folder.count !== undefined && (
                  <span className="text-[10px] text-muted-foreground bg-muted rounded px-1.5 py-0.5 flex-none">{folder.count}</span>
                )}
              </div>
              <span className="w-24 hidden sm:block" />
              <span className="w-28 text-right text-xs text-muted-foreground">{folder.modified ?? '—'}</span>
              <span className="w-24 hidden md:block" />
            </div>
          ))}
        </div>
      )}

      {hasFolders && hasFiles && <div className="border-t border-border/60 mx-4 my-1" />}

      {hasFiles && (
        <div className="divide-y divide-border/40">
          {files.map(file => {
            const typeCfg = file.docType ? DOC_TYPE_CONFIG[file.docType] : null
            return (
              <div
                key={file.id}
                data-item-id={file.id}
                onClick={() => onSelect(file)}
                onDoubleClick={() => onOpen(file)}
                className={cn(
                  'flex items-start px-4 py-3 transition-colors cursor-default select-none',
                  selectedId === file.id
                    ? 'bg-primary/10 border-l-2 border-l-primary'
                    : 'hover:bg-muted/40 border-l-2 border-l-transparent'
                )}
              >
                <div className="flex items-start gap-3 flex-1 min-w-0">
                  {file.docType ? (
                    <FileTypeIcon type={file.docType} className="mt-0.5 flex-none" />
                  ) : (
                    <FileText className="w-4 h-4 text-muted-foreground mt-0.5 flex-none" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium truncate">{file.name}</span>
                      {typeCfg && (
                        <span className={cn('text-[10px] px-1.5 py-0.5 rounded border flex-none hidden sm:inline', typeCfg.badgeClass)}>
                          {typeCfg.label}
                        </span>
                      )}
                    </div>
                    {file.excerpt && (
                      <p className="text-xs text-muted-foreground line-clamp-1 mt-0.5">{file.excerpt}</p>
                    )}
                  </div>
                </div>
                <span className="w-28 text-right text-xs text-muted-foreground flex-none pt-0.5">{file.modified ?? '—'}</span>
                <span className="w-24 text-right text-xs text-muted-foreground flex-none pt-0.5 hidden md:block">{file.author ?? '—'}</span>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

// ─── Global Open Tabs ─────────────────────────────────────────────────────────

type GlobalTabType = 'note' | 'work-spec' | 'voice-memo' | 'basecamp-view' | 'folder-view'

type GlobalTab = {
  id: string
  title: string
  type: GlobalTabType
  workspaceId: string
  dirty?: boolean
}

const ALL_MOCK_TABS: GlobalTab[] = [
  // Hop Studios
  { id: 'gt-1', title: 'Weekly Triage',              type: 'basecamp-view', workspaceId: 'hop-studios' },
  { id: 'gt-2', title: 'Wide ad management system',  type: 'work-spec',     workspaceId: 'hop-studios' },
  { id: 'gt-3', title: 'clients/',                   type: 'folder-view',   workspaceId: 'hop-studios' },
  // Personal
  { id: 'gt-4', title: 'Tasks: Rowan',               type: 'note',          workspaceId: 'personal' },
  // G-School
  { id: 'gt-5', title: 'Faculty engagement todos',   type: 'work-spec',     workspaceId: 'gschool' },
]

function GlobalOpenTabsSection({ activeWsIds }: { activeWsIds: Set<string> }) {
  const [collapsed, setCollapsed] = useState(false)
  const [activeTabId, setActiveTabId] = useState<string | null>('gt-1')
  const [closedIds, setClosedIds] = useState<Set<string>>(new Set())

  const visibleTabs = ALL_MOCK_TABS.filter(t => !closedIds.has(t.id))
  const totalCount = visibleTabs.length

  const iconFor = (type: GlobalTabType) => {
    if (type === 'work-spec')     return <FileText className="h-3 w-3 text-blue-400 flex-none" />
    if (type === 'voice-memo')    return <Mic className="h-3 w-3 text-purple-400 flex-none" />
    if (type === 'basecamp-view') return <BasecampIcon className="h-3 w-3 flex-none text-muted-foreground" />
    if (type === 'folder-view')   return <Folder className="h-3 w-3 text-muted-foreground flex-none" />
    return <FileText className="h-3 w-3 text-muted-foreground flex-none" />
  }

  // Group tabs by workspace, preserving WORKSPACES order
  const groups = WORKSPACES
    .filter(ws => visibleTabs.some(t => t.workspaceId === ws.id))
    .map(ws => ({ ws, tabs: visibleTabs.filter(t => t.workspaceId === ws.id) }))

  return (
    <div data-testid="browse-nav-open-tabs" className="flex-shrink-0 border-b border-border bg-sidebar">
      {/* Section header */}
      <div className="flex items-center gap-1 px-2 py-1.5">
        <button
          onClick={() => setCollapsed(c => !c)}
          className="flex items-center gap-1 flex-1 min-w-0 transition-colors text-sidebar-foreground/60 hover:text-sidebar-foreground"
        >
          {collapsed
            ? <ChevronRight className="h-3 w-3 flex-none" />
            : <ChevronDown className="h-3 w-3 flex-none" />}
          <span className="text-[10px] font-semibold uppercase tracking-wider">Open Tabs</span>
        </button>
        <span className="text-[10px] text-sidebar-foreground/40 flex-none">{totalCount}</span>
      </div>

      {/* Grouped tab list */}
      {!collapsed && (
        <div className="pb-1">
          {groups.map(({ ws, tabs }) => {
            const WsIcon = ws.icon
            const dimmed = !activeWsIds.has(ws.id)
            return (
              <div key={ws.id} data-testid={`browse-nav-workspace-${ws.id}`}>
                {/* Workspace group header */}
                <div
                  className="flex items-center gap-1.5 px-2 py-0.5 mt-0.5"
                  style={{ borderLeft: `2px solid ${ws.color}60` }}
                >
                  <span
                    className="w-3.5 h-3.5 rounded flex items-center justify-center flex-none"
                    style={{ backgroundColor: ws.color + '25' }}
                  >
                    <WsIcon className="w-2 h-2" style={{ color: ws.color }} />
                  </span>
                  <span
                    className={cn('text-[9px] font-semibold uppercase tracking-wider truncate', dimmed ? 'text-muted-foreground/50' : 'text-muted-foreground/70')}
                  >
                    {ws.name}
                  </span>
                </div>
                {/* Tabs in this group */}
                {tabs.map(tab => {
                  const isActive = activeTabId === tab.id
                  return (
                    <div
                      key={tab.id}
                      onClick={() => setActiveTabId(tab.id)}
                      className={cn(
                        'group flex items-center gap-1.5 pr-2 py-[3px] text-xs cursor-pointer transition-colors border-l-2',
                        isActive
                          ? 'bg-sidebar-accent text-sidebar-foreground font-medium'
                          : 'text-sidebar-foreground/70 hover:bg-sidebar-accent/50 hover:text-sidebar-foreground',
                      )}
                      style={{
                        paddingLeft: '22px',
                        borderLeftColor: isActive ? ws.color : 'transparent',
                      }}
                    >
                      <button
                        onClick={e => { e.stopPropagation(); setClosedIds(prev => new Set([...prev, tab.id])) }}
                        className={cn('flex-none p-0.5 rounded transition-opacity', isActive ? 'opacity-100' : 'opacity-0 group-hover:opacity-100')}
                      >
                        <X className="h-2.5 w-2.5" />
                      </button>
                      {iconFor(tab.type)}
                      <span className="flex-1 truncate">
                        {tab.title}
                        {tab.dirty && <span className="ml-1" style={{ color: ws.color }}>•</span>}
                      </span>
                    </div>
                  )
                })}
              </div>
            )
          })}
          {totalCount === 0 && (
            <p className="text-[11px] text-muted-foreground/50 px-3 py-1 italic">No open tabs</p>
          )}
        </div>
      )}
    </div>
  )
}

// ─── BasecampPane ─────────────────────────────────────────────────────────────

const BC_NAV_ITEMS = [
  { id: 'bc-bookmarks', label: 'Bookmarks',  icon: Bookmark,      badge: undefined },
  { id: 'bc-mystuff',   label: 'My Stuff',   icon: User2,         badge: 90 },
  { id: 'bc-todos',     label: 'Todos',      icon: CheckSquare,   badge: 7 },
  { id: 'bc-projects',  label: 'Projects',   icon: Boxes,         badge: undefined },
  { id: 'bc-triage',    label: 'Triage',     icon: CalendarClock, badge: undefined },
] as const

function BasecampPane({ workspace, onClose }: { workspace: WorkspaceDef; onClose: () => void }) {
  const [activeItem, setActiveItem] = useState<string>('bc-todos')

  return (
    <div data-testid="browse-nav-basecamp-pane" className="flex flex-col h-full overflow-hidden">
      {/* Workspace-color top accent */}
      <div className="flex-shrink-0 h-[3px]" style={{ backgroundColor: workspace.color }} />

      {/* Header */}
      <div className="flex items-center gap-1.5 px-2 py-1.5 border-b border-border flex-shrink-0 bg-sidebar">
        <BasecampIcon className="w-3.5 h-3.5 flex-none" style={{ color: workspace.color }} />
        <span
          className="flex-1 text-[11px] font-medium truncate"
          style={{ color: workspace.color + 'cc' }}
        >
          Basecamp · {workspace.name}
        </span>
        <button
          onClick={onClose}
          className="flex-none p-0.5 rounded hover:bg-sidebar-accent transition-colors text-muted-foreground hover:text-sidebar-foreground"
        >
          <X className="w-3 h-3" />
        </button>
      </div>

      {/* Nav items */}
      <div className="flex-1 overflow-y-auto py-1 min-h-0">
        {BC_NAV_ITEMS.map(item => {
          const ItemIcon = item.icon
          const isActive = activeItem === item.id
          return (
            <div
              key={item.id}
              onClick={() => setActiveItem(item.id)}
              className={cn(
                'group flex items-center gap-2 px-3 py-1.5 text-xs cursor-pointer transition-colors border-l-2',
                isActive
                  ? 'bg-sidebar-accent text-sidebar-foreground font-medium'
                  : 'text-sidebar-foreground/70 hover:bg-sidebar-accent/60 hover:text-sidebar-foreground border-transparent'
              )}
              style={{ borderLeftColor: isActive ? workspace.color : 'transparent' }}
            >
              <ItemIcon className="w-3.5 h-3.5 flex-none" style={isActive ? { color: workspace.color } : undefined} />
              <span className="flex-1 truncate">{item.label}</span>
              {item.badge !== undefined && (
                <span className="flex-none text-[10px] font-medium rounded-full px-1.5 py-0.5 leading-none"
                  style={{ backgroundColor: workspace.color + '20', color: workspace.color }}>
                  {item.badge > 99 ? '99+' : item.badge}
                </span>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ─── WorkspaceDocsPane ────────────────────────────────────────────────────────

interface WorkspaceDocsPaneProps {
  workspace: WorkspaceDef
  onClose: () => void
  onOpen: (node: FolderNode) => void
}

function WorkspaceDocsPane({ workspace, onClose, onOpen }: WorkspaceDocsPaneProps) {
  const [breadcrumb, setBreadcrumb] = useState<FolderNode[]>([])
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const tree = MOCK_TREE[workspace.id] ?? []
  const currentNodes = breadcrumb.length > 0 ? (breadcrumb[breadcrumb.length - 1].children ?? []) : tree

  const breadcrumbPath = ['Documents', ...breadcrumb.map(b => b.name)]

  return (
    <div data-testid="browse-nav-docs-pane" className="flex flex-col h-full overflow-hidden">
      {/* Workspace-color top accent — visual tie to workspace above, no label needed */}
      <div className="flex-shrink-0 h-[3px]" style={{ backgroundColor: workspace.color }} />

      {/* Breadcrumb / path header */}
      <div className="flex items-center gap-1 px-2 py-1.5 border-b border-border flex-shrink-0 min-w-0 bg-sidebar">
        {breadcrumb.length > 0 ? (
          <button
            onClick={() => setBreadcrumb(p => p.slice(0, -1))}
            className="flex-none p-0.5 rounded hover:bg-sidebar-accent transition-colors"
          >
            <ChevronLeft className="w-3 h-3 text-muted-foreground" />
          </button>
        ) : (
          <span className="w-[18px] flex-none" />
        )}
        <span
          className="flex-1 text-[11px] text-muted-foreground truncate"
          style={{ color: breadcrumb.length > 0 ? undefined : workspace.color + 'cc' }}
        >
          {breadcrumbPath.join(' / ')}
        </span>
        <button
          onClick={onClose}
          className="flex-none p-0.5 rounded hover:bg-sidebar-accent transition-colors text-muted-foreground hover:text-sidebar-foreground"
        >
          <X className="w-3 h-3" />
        </button>
      </div>

      {/* Tree — scoped to this workspace, color-tinted folder icons */}
      <div className="flex-1 overflow-y-auto py-1 min-h-0">
        {currentNodes.length > 0 ? currentNodes.map(node => (
          <TreeFileNode
            key={node.id}
            node={node}
            wsColor={workspace.color}
            selectedId={selectedId}
            onSelect={n => setSelectedId(n.id)}
            onOpen={n => {
              if (n.type === 'folder' && n.children) {
                setBreadcrumb(p => [...p, n])
              } else {
                onOpen(n)
              }
            }}
          />
        )) : (
          <div className="px-4 py-6 text-xs text-muted-foreground text-center">No items here</div>
        )}
      </div>
    </div>
  )
}

// ─── BROWSE panel ─────────────────────────────────────────────────────────────

function BrowsePanel({ activeNav, expandedWorkspaces, expandedExtensions, treeMode, lowerPane, activeWsIds, onNavigate, onToggleWorkspace, onToggleExtension, onToggleTreeMode, onToggleLowerPane }: {
  activeNav: NavItem
  expandedWorkspaces: Set<string>
  expandedExtensions: Set<string>
  treeMode: Set<string>
  lowerPane: LowerPane
  activeWsIds: Set<string>
  onNavigate: (item: NavItem) => void
  onToggleWorkspace: (id: string) => void
  onToggleExtension: (wsId: string, extId: string) => void
  onToggleTreeMode: (wsId: string) => void
  onToggleLowerPane: (kind: 'docs' | 'basecamp', wsId: string) => void
}) {
  const activeKey = navKey(activeNav)
  const visibleWorkspaces = WORKSPACES.filter(w => activeWsIds.has(w.id))
  return (
    <div data-testid="browse-nav-browse-panel" className="flex flex-col h-full overflow-hidden">
      <div className="px-3 py-2 border-b border-border">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Browse</p>
      </div>
      <div className="flex-1 overflow-y-auto px-2 py-2 space-y-0.5">
        <NavRow icon={Home} label="Home" active={activeKey === 'home'} onClick={() => onNavigate({ kind: 'home' })} />
        <NavRow icon={Inbox} label="Inbox" badge={3} active={activeKey === 'inbox'} onClick={() => onNavigate({ kind: 'inbox' })} />

        <div className="px-2 pt-3 pb-1 flex items-center justify-between">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Workspaces</span>
          <button className="p-0.5 rounded hover:bg-sidebar-accent text-muted-foreground hover:text-sidebar-foreground"><Plus className="w-3 h-3" /></button>
        </div>

        {visibleWorkspaces.map(ws => (
          <WorkspaceSection
            key={ws.id}
            workspace={ws}
            expandedWorkspaces={expandedWorkspaces}
            expandedExtensions={expandedExtensions}
            activeNav={activeNav}
            treeMode={treeMode}
            lowerPane={lowerPane}
            onToggleWorkspace={onToggleWorkspace}
            onToggleExtension={onToggleExtension}
            onNavigate={onNavigate}
            onToggleTreeMode={onToggleTreeMode}
            onToggleLowerPane={onToggleLowerPane}
          />
        ))}

        <div className="px-2 pt-3 pb-1">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Tools</span>
        </div>
        <NavRow icon={Bell} label="Notifications" badge={3} active={activeKey === 'global-notifications'} onClick={() => onNavigate({ kind: 'global', id: 'notifications' })} />
        <NavRow icon={Tag} label="Tags" active={activeKey === 'global-tags'} onClick={() => onNavigate({ kind: 'global', id: 'tags' })} />
        <NavRow icon={Network} label="Graph" active={activeKey === 'global-graph'} onClick={() => onNavigate({ kind: 'global', id: 'graph' })} />
        <NavRow icon={Trash2} label="Trash" active={activeKey === 'global-trash'} onClick={() => onNavigate({ kind: 'global', id: 'trash' })} />
      </div>
    </div>
  )
}

// ─── SEARCH panel ─────────────────────────────────────────────────────────────

const MOCK_RESULTS = [
  { id: '1', title: 'GA4 GTM recommendations', path: 'hop-studios/clients/greater-good/ggsc/todos/', match: '…recommend implementing <mark>GA4</mark> enhanced ecommerce…', ws: 'Hop Studios', wsColor: '#6366f1', time: '2h ago' },
  { id: '2', title: 'Wide ad management system', path: 'hop-studios/clients/greater-good/greatergood/todos/', match: '…<mark>ad management</mark> system for site-wide ad placements…', ws: 'Hop Studios', wsColor: '#6366f1', time: '4h ago' },
  { id: '3', title: 'G-School faculty engagement', path: 'gschool/meetings/', match: '…open <mark>todos</mark> from faculty engagement session…', ws: 'G-School', wsColor: '#f59e0b', time: 'Yesterday' },
  { id: '4', title: 'volleyball reference letter', path: 'personal/volleyball/', match: '…draft reference letter for <mark>Samara Brown</mark>…', ws: 'Personal', wsColor: '#10b981', time: '2 days ago' },
]

function SearchPanel() {
  const [query, setQuery] = useState('')
  const [scope, setScope] = useState<string>('all')
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => { inputRef.current?.focus() }, [])

  const scopes = [
    { id: 'all', label: 'All' },
    { id: 'hop-studios', label: 'Hop Studios' },
    { id: 'personal', label: 'Personal' },
    { id: 'gschool', label: 'G-School' },
  ]

  const filtered = query.trim()
    ? MOCK_RESULTS.filter(r =>
        scope === 'all' || r.ws.toLowerCase().replace(/\s/g, '-') === scope ||
        (scope === 'hop-studios' && r.ws === 'Hop Studios') ||
        (scope === 'personal' && r.ws === 'Personal') ||
        (scope === 'gschool' && r.ws === 'G-School')
      )
    : []

  return (
    <div data-testid="browse-nav-search-panel" className="flex flex-col h-full overflow-hidden">
      <div className="px-3 py-2 border-b border-border">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Search</p>
      </div>

      {/* Input */}
      <div className="px-3 pt-3 pb-2">
        <div className="flex items-center gap-2 rounded-md border border-border bg-background px-3 py-2 focus-within:border-primary/50 transition-colors">
          <Search className="w-3.5 h-3.5 text-muted-foreground flex-none" />
          <input
            ref={inputRef}
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search everything..."
            className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
          />
          {query && (
            <button onClick={() => setQuery('')} className="text-muted-foreground hover:text-foreground">
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>

      {/* Scope chips */}
      <div className="px-3 pb-2 flex gap-1 flex-wrap">
        {scopes.map(s => (
          <button
            key={s.id}
            onClick={() => setScope(s.id)}
            className={cn(
              'px-2.5 py-1 rounded-full text-xs transition-colors border',
              scope === s.id
                ? 'bg-primary text-primary-foreground border-primary'
                : 'bg-muted text-muted-foreground border-border hover:border-primary/40 hover:text-foreground'
            )}
          >
            {s.label}
          </button>
        ))}
      </div>

      <div className="border-t border-border" />

      {/* Results */}
      <div className="flex-1 overflow-y-auto">
        {!query.trim() ? (
          <div className="px-4 py-8 text-center">
            <Search className="w-8 h-8 text-muted-foreground/30 mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">Start typing to search across all workspaces</p>
            <p className="text-xs text-muted-foreground/60 mt-1">Filter by workspace using the chips above</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="px-4 py-8 text-center">
            <p className="text-sm text-muted-foreground">No results for "{query}"</p>
          </div>
        ) : (
          <div className="py-2">
            <div className="px-3 py-1 text-[10px] text-muted-foreground">{filtered.length} result{filtered.length !== 1 ? 's' : ''}</div>
            {filtered.map(result => (
              <div key={result.id} className="px-3 py-2.5 hover:bg-sidebar-accent/60 cursor-pointer transition-colors border-b border-border/30 last:border-0">
                <div className="flex items-center gap-2 mb-1">
                  <FileText className="w-3.5 h-3.5 text-muted-foreground flex-none" />
                  <span className="text-sm font-medium truncate flex-1">{result.title}</span>
                  <span className="text-[10px] text-muted-foreground">{result.time}</span>
                </div>
                <div className="text-[10px] text-muted-foreground mb-1 truncate" style={{ paddingLeft: '19px' }}>{result.path}</div>
                <div
                  className="text-xs text-muted-foreground/80 line-clamp-1"
                  style={{ paddingLeft: '19px' }}
                  dangerouslySetInnerHTML={{ __html: result.match.replace(/<mark>(.*?)<\/mark>/g, '<span style="background:rgba(99,102,241,0.25);color:inherit;border-radius:2px;padding:0 2px">$1</span>') }}
                />
                <div className="mt-1" style={{ paddingLeft: '19px' }}>
                  <span className="inline-flex items-center gap-1 text-[9px] text-muted-foreground bg-muted rounded px-1.5 py-0.5">
                    <span style={{ width: 6, height: 6, borderRadius: '50%', background: result.wsColor, display: 'inline-block' }} />
                    {result.ws}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

// ─── WORKSPACES panel ─────────────────────────────────────────────────────────

function WorkspacesPanel() {
  const [enabled, setEnabled] = useState<Set<string>>(new Set(['hop-studios', 'personal']))

  const toggle = (id: string) => setEnabled(prev => {
    const next = new Set(prev)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    return next
  })

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <div className="px-3 py-2 border-b border-border">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Workspaces</p>
      </div>
      <div className="flex-1 overflow-y-auto py-2 px-2">
        <p className="px-2 pb-2 text-xs text-muted-foreground">Active workspaces appear in Browse. Disable to hide.</p>
        <div className="space-y-1">
          {WORKSPACES.map(ws => {
            const WsIcon = ws.icon
            const isOn = enabled.has(ws.id)
            return (
              <div key={ws.id} className="flex items-center gap-3 px-3 py-2.5 rounded-lg border border-border bg-card hover:border-border/80 transition-colors">
                <WsIcon className="w-4 h-4 flex-none" style={{ color: ws.color }} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{ws.name}</div>
                  <div className="text-[10px] text-muted-foreground flex items-center gap-1.5 mt-0.5">
                    <span>{ws.fileCount} docs</span>
                    {ws.extensions.length > 0 && (
                      <>
                        <span>·</span>
                        {ws.extensions.map(ext => {
                          const ExtIcon = ext.icon
                          return (
                            <span key={ext.id} className="inline-flex items-center gap-0.5">
                              <ExtIcon className="w-2.5 h-2.5" />
                              {ext.label}
                            </span>
                          )
                        })}
                      </>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => toggle(ws.id)}
                  className={cn('flex-none transition-colors', isOn ? 'text-primary' : 'text-muted-foreground')}
                  title={isOn ? 'Disable workspace' : 'Enable workspace'}
                >
                  {isOn ? <ToggleRight className="w-5 h-5" /> : <ToggleLeft className="w-5 h-5" />}
                </button>
              </div>
            )
          })}
        </div>

        <div className="mt-3 px-2">
          <button className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg border border-dashed border-border text-xs text-muted-foreground hover:border-primary/40 hover:text-foreground transition-colors">
            <Plus className="w-3.5 h-3.5" />
            Add workspace
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Content panel (right side) ───────────────────────────────────────────────

function ContentPanel({ nav, treeMode, onToggleTreeMode }: { nav: NavItem; treeMode: Set<string>; onToggleTreeMode: (wsId: string) => void }) {
  const getWs = (id: string) => WORKSPACES.find(w => w.id === id)

  // ── Inbox ──
  if (nav.kind === 'inbox') {
    const MOCK_INBOX = [
      { id: '1', type: 'voice_memo', title: 'Recording — Mar 5 client meeting', body: 'Cloudflare migration timeline, confirm pricing with Berkeley Unix team by EOW.', source: 'Voice Memo Pipeline', time: '2h ago', unread: true },
      { id: '2', type: 'agent_message', title: 'Daily digest — Wed Mar 5', body: 'You have 3 overdue todos, 2 due today. Recommended focus: test-verify-site-new-servers.', source: 'FlowNotes Agent', time: '4h ago', unread: true },
      { id: '3', type: 'flow_output', title: 'Weekly triage completed', body: '12 todos reviewed, 4 dates pushed, 2 flagged as blocked.', source: 'FlowJob: weekly-triage', time: 'Yesterday', unread: false },
      { id: '4', type: 'suggestion', title: 'Work spec not updated in 14 days', body: 'Wide ad management system README last touched Feb 19. Basecamp todo has new activity.', source: 'FlowNotes Heuristics', time: '2 days ago', unread: false },
    ]
    const typeColors: Record<string, string> = {
      voice_memo: 'text-purple-400', agent_message: 'text-blue-400', flow_output: 'text-green-400', suggestion: 'text-yellow-400',
    }
    const typeLabels: Record<string, string> = {
      voice_memo: 'Voice Memo', agent_message: 'From Agent', flow_output: 'Flow Output', suggestion: 'Suggestion',
    }
    return (
      <div className="flex flex-col h-full">
        <div className="px-6 pt-6 pb-4 border-b border-border flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Inbox className="w-5 h-5 text-muted-foreground" />
            <h1 className="text-xl font-bold">Inbox</h1>
            <span className="text-xs font-medium bg-primary/15 text-primary rounded-full px-2 py-0.5">2 unread</span>
          </div>
        </div>
        <div className="px-6 py-4 flex-1 overflow-y-auto">
          <div className="max-w-2xl space-y-2">
            {MOCK_INBOX.map(item => (
              <div key={item.id} className={cn('border rounded-lg p-4 bg-card transition-colors', item.unread ? 'border-l-2 border-l-primary border-border' : 'border-border')}>
                <div className="flex items-start gap-3">
                  <div className={cn('mt-0.5', typeColors[item.type])}>
                    {item.type === 'voice_memo' && <span className="text-sm">🎤</span>}
                    {item.type === 'agent_message' && <span className="text-sm">🤖</span>}
                    {item.type === 'flow_output' && <span className="text-sm">⚡</span>}
                    {item.type === 'suggestion' && <span className="text-sm">💡</span>}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2 mb-0.5">
                      <div className="flex items-center gap-2">
                        {item.unread && <span className="w-1.5 h-1.5 rounded-full bg-primary flex-none" />}
                        <span className="text-sm font-medium truncate">{item.title}</span>
                      </div>
                      <span className="text-[10px] text-muted-foreground whitespace-nowrap">{item.time}</span>
                    </div>
                    <p className="text-xs text-muted-foreground line-clamp-2 mb-2">{item.body}</p>
                    <div className="flex items-center gap-1.5">
                      <span className="text-[10px] px-1.5 py-0.5 rounded border bg-muted text-muted-foreground border-border font-medium">{typeLabels[item.type]}</span>
                      <span className="text-[10px] text-muted-foreground/60">{item.source}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  // ── Home ──
  if (nav.kind === 'home') {
    return (
      <div className="p-6 max-w-2xl">
        <h1 className="text-xl font-bold mb-1">Home</h1>
        <p className="text-sm text-muted-foreground mb-6">Your workspace hub — recent activity, quick access.</p>
        <div className="grid grid-cols-3 gap-3 mb-6">
          {WORKSPACES.map(ws => {
            const WsIcon = ws.icon
            return (
              <div key={ws.id} className="border border-border rounded-lg p-4 bg-card hover:border-primary/30 cursor-pointer transition-colors">
                <div className="flex items-center gap-2 mb-2">
                  <WsIcon className="w-4 h-4" style={{ color: ws.color }} />
                  <span className="font-medium text-sm">{ws.name}</span>
                </div>
                <div className="text-xs text-muted-foreground">{ws.fileCount} documents</div>
                {ws.extensions.length > 0 && (
                  <div className="flex gap-1 mt-2 flex-wrap">
                    {ws.extensions.map(ext => {
                      const ExtIcon = ext.icon
                      return (
                        <span key={ext.id} className="inline-flex items-center gap-1 text-[10px] text-muted-foreground bg-muted rounded px-1.5 py-0.5">
                          <ExtIcon className="w-2.5 h-2.5" />{ext.label}
                        </span>
                      )
                    })}
                  </div>
                )}
              </div>
            )
          })}
        </div>
        <div className="border border-border rounded-lg p-4 bg-card">
          <h2 className="text-sm font-semibold mb-3">Recent</h2>
          {[
            { title: '2026-03-03 Star faculty engagement open todos', ws: 'G-School', time: '2h ago' },
            { title: 'GGIA GTM/GA4 recommendations', ws: 'Hop Studios', time: '4h ago' },
            { title: 'Wide ad management system', ws: 'Hop Studios', time: 'Yesterday' },
          ].map(item => (
            <div key={item.title} className="flex items-center gap-3 py-2 border-b border-border/50 last:border-0 cursor-pointer hover:text-primary transition-colors">
              <FileText className="w-3.5 h-3.5 text-muted-foreground flex-none" />
              <span className="flex-1 text-sm truncate">{item.title}</span>
              <span className="text-xs text-muted-foreground">{item.ws}</span>
              <span className="text-xs text-muted-foreground">{item.time}</span>
            </div>
          ))}
        </div>
      </div>
    )
  }

  // ── Documents ──
  if (nav.kind === 'files') {
    const ws = getWs(nav.workspaceId)
    if (!ws) return null
    return (
      <DocumentsExplorer
        workspace={ws}
        treeMode={treeMode.has(nav.workspaceId)}
        onToggleTreeMode={() => onToggleTreeMode(nav.workspaceId)}
      />
    )
  }

  // ── Extension / extension-child ──
  if (nav.kind === 'extension' || nav.kind === 'extension-child') {
    const ws = getWs(nav.workspaceId)
    const childId = nav.kind === 'extension-child' ? nav.childId : null

    // ── Basecamp ──
    if (nav.extensionId === 'basecamp') {
      const Breadcrumb = () => (
        <div className="flex items-center gap-2 mb-1">
          {ws && <ws.icon className="w-4 h-4" style={{ color: ws.color }} />}
          <span className="text-sm text-muted-foreground">{ws?.name}</span>
          <ChevronRight className="w-3 h-3 text-muted-foreground" />
          <Link2 className="w-4 h-4 text-blue-400" />
          <h1 className="text-xl font-bold">Basecamp</h1>
        </div>
      )

      // Bookmarks
      if (!childId || childId === 'bc-bookmarks') {
        return (
          <div className="p-6 max-w-2xl">
            <Breadcrumb />
            <p className="text-sm text-muted-foreground mb-4">Bookmarks — pinned todos (right-click any todo to bookmark)</p>
            <div className="border border-border rounded-lg p-8 text-center bg-card">
              <Bookmark className="w-8 h-8 text-muted-foreground/30 mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">No bookmarked items</p>
              <p className="text-xs text-muted-foreground/60 mt-1">Right-click a todo to bookmark it</p>
            </div>
          </div>
        )
      }

      // My Stuff
      if (childId === 'bc-mystuff') {
        return (
          <div className="p-6 max-w-2xl">
            <Breadcrumb />
            <p className="text-sm text-muted-foreground mb-4">My Stuff — 90 todos assigned to you across all projects</p>
            <div className="space-y-2">
              {[
                { title: 'Test verify site on new servers', project: '~Hop Business', due: '2026-03-05', late: true },
                { title: 'Review website tracking & meta pixel privacy', project: 'Greater Good', due: '2026-03-08', late: false },
                { title: 'Update faculty engagement tool language', project: 'G-School', due: '2026-03-10', late: false },
                { title: 'Set up ScalePost CDN integration', project: 'GreaterGood.org', due: '2026-03-12', late: false },
                { title: 'Monitor and protect traffic spikes', project: 'GreaterGood.org', due: '2026-03-15', late: false },
              ].map(todo => (
                <div key={todo.title} className="border border-border rounded-lg px-4 py-3 bg-card flex items-center gap-3 cursor-pointer hover:border-primary/30 transition-colors">
                  <CheckSquare className="w-4 h-4 text-muted-foreground flex-none" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm truncate">{todo.title}</div>
                    <div className="text-[10px] text-muted-foreground mt-0.5">{todo.project}</div>
                  </div>
                  <span className={cn('text-xs', todo.late ? 'text-red-400' : 'text-muted-foreground')}>{todo.due}</span>
                </div>
              ))}
              <p className="text-xs text-muted-foreground text-center pt-2">+ 85 more todos</p>
            </div>
          </div>
        )
      }

      // Todos
      if (childId === 'bc-todos') {
        return (
          <div className="p-6 max-w-2xl">
            <Breadcrumb />
            <p className="text-sm text-muted-foreground mb-4">Todos — 7 open in {ws?.name}</p>
            <div className="space-y-2">
              {[
                { title: 'Test verify site on new servers', due: '2026-03-05', priority: 'high' },
                { title: 'Review website tracking & meta pixel privacy', due: '2026-03-08', priority: 'medium' },
                { title: 'Update faculty engagement tool language', due: '2026-03-10', priority: 'medium' },
                { title: 'Monitor and protect traffic spikes', due: '2026-03-15', priority: 'low' },
                { title: 'Add Google Ads tag GGIA & GGMag', due: '2026-03-18', priority: 'low' },
                { title: 'Provide recommendations: social sign-on GGIA', due: '2026-03-20', priority: 'low' },
                { title: 'Investigate Cloudflare viability', due: '2026-03-25', priority: 'low' },
              ].map(todo => (
                <div key={todo.title} className="border border-border rounded-lg px-4 py-3 bg-card flex items-center gap-3 cursor-pointer hover:border-primary/30 transition-colors">
                  <CheckSquare className="w-4 h-4 text-muted-foreground flex-none" />
                  <span className="flex-1 text-sm">{todo.title}</span>
                  <span className="text-xs text-muted-foreground">{todo.due}</span>
                  <span className={cn('text-[10px] px-1.5 py-0.5 rounded border', todo.priority === 'high' ? 'bg-red-500/10 text-red-400 border-red-500/20' : todo.priority === 'medium' ? 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20' : 'bg-muted text-muted-foreground border-border')}>
                    {todo.priority}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )
      }

      // Projects
      if (childId === 'bc-projects') {
        return (
          <div className="p-6 max-w-2xl">
            <Breadcrumb />
            <p className="text-sm text-muted-foreground mb-4">Projects — all Basecamp projects in {ws?.name}</p>
            <div className="mb-3">
              <div className="flex items-center gap-2 rounded-md border border-border bg-background px-3 py-2">
                <Search className="w-3.5 h-3.5 text-muted-foreground" />
                <input placeholder="Search projects..." className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground" />
              </div>
            </div>
            <div className="space-y-2">
              {[
                { name: '~Hop Business', active: 17, synced: false },
                { name: 'Greater Good Science Center', active: 12, synced: true },
                { name: 'GreaterGood.org', active: 8, synced: true },
                { name: 'G-School', active: 4, synced: true },
                { name: 'ADDCA', active: 0, synced: false },
              ].map(project => (
                <div key={project.name} className="border border-border rounded-lg px-4 py-3 bg-card flex items-center gap-3 cursor-pointer hover:border-primary/30 transition-colors">
                  <Boxes className="w-4 h-4 text-muted-foreground flex-none" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm">{project.name}</div>
                    <div className="text-[10px] text-muted-foreground mt-0.5">{project.active} active · Last sync: {project.synced ? 'Just now' : 'Never'}</div>
                  </div>
                  <Settings className="w-3.5 h-3.5 text-muted-foreground" />
                </div>
              ))}
            </div>
          </div>
        )
      }

      // Triage
      if (childId === 'bc-triage') {
        return (
          <div className="p-6 max-w-2xl">
            <Breadcrumb />
            <p className="text-sm text-muted-foreground mb-4">Triage — overdue & due-this-week todos for {ws?.name}</p>
            <div className="border border-red-500/20 bg-red-500/5 rounded-lg p-4 mb-4">
              <div className="flex items-center gap-2 mb-3">
                <CalendarClock className="w-4 h-4 text-red-400" />
                <span className="text-sm font-medium text-red-400">3 overdue</span>
              </div>
              {[
                { title: 'Test verify site on new servers', was: '2026-03-01' },
                { title: 'Wide ad management system kickoff', was: '2026-02-28' },
                { title: 'Audit and remove secure diagnostic info files', was: '2026-02-25' },
              ].map(todo => (
                <div key={todo.title} className="flex items-center gap-3 py-1.5 border-b border-border/30 last:border-0">
                  <CheckSquare className="w-3.5 h-3.5 text-red-400 flex-none" />
                  <span className="flex-1 text-sm">{todo.title}</span>
                  <span className="text-[10px] text-red-400">was {todo.was}</span>
                </div>
              ))}
            </div>
            <div className="border border-yellow-500/20 bg-yellow-500/5 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3">
                <CalendarClock className="w-4 h-4 text-yellow-400" />
                <span className="text-sm font-medium text-yellow-400">4 due this week</span>
              </div>
              {[
                { title: 'Review website tracking & meta pixel privacy', due: '2026-03-08' },
                { title: 'Update faculty engagement tool language', due: '2026-03-09' },
                { title: 'Set up ScalePost CDN integration', due: '2026-03-10' },
                { title: 'Investigate Cloudflare viability', due: '2026-03-11' },
              ].map(todo => (
                <div key={todo.title} className="flex items-center gap-3 py-1.5 border-b border-border/30 last:border-0">
                  <CheckSquare className="w-3.5 h-3.5 text-yellow-400 flex-none" />
                  <span className="flex-1 text-sm">{todo.title}</span>
                  <span className="text-[10px] text-yellow-400">{todo.due}</span>
                </div>
              ))}
            </div>
          </div>
        )
      }

      // Basecamp top-level (no child selected — show overview)
      return (
        <div className="p-6 max-w-2xl">
          <Breadcrumb />
          <p className="text-sm text-muted-foreground mb-4">Connected to {ws?.name} · Select a section from the sidebar</p>
          <div className="grid grid-cols-2 gap-3">
            {[
              { id: 'bc-bookmarks', label: 'Bookmarks', icon: Bookmark, desc: 'Pinned todos', count: 0 },
              { id: 'bc-mystuff',   label: 'My Stuff',  icon: User2,      desc: 'Assigned to me', count: 90 },
              { id: 'bc-todos',     label: 'Todos',     icon: CheckSquare, desc: 'Open todos', count: 7 },
              { id: 'bc-projects',  label: 'Projects',  icon: Boxes,       desc: 'All projects', count: 88 },
              { id: 'bc-triage',    label: 'Triage',    icon: CalendarClock, desc: 'Overdue & this week', count: 7 },
            ].map(item => {
              const ItemIcon = item.icon
              return (
                <div key={item.id} className="border border-border rounded-lg p-4 bg-card hover:border-primary/30 cursor-pointer transition-colors">
                  <div className="flex items-center gap-2 mb-1">
                    <ItemIcon className="w-4 h-4 text-blue-400" />
                    <span className="font-medium text-sm">{item.label}</span>
                    {item.count > 0 && <span className="ml-auto text-[10px] font-medium bg-primary/15 text-primary rounded-full px-1.5 py-0.5">{item.count}</span>}
                  </div>
                  <p className="text-xs text-muted-foreground">{item.desc}</p>
                </div>
              )
            })}
          </div>
        </div>
      )
    }

    // ── Product Board ──
    if (nav.extensionId === 'product-board') {
      const view = childId === 'pb-next' ? 'Next' : childId === 'pb-later' ? 'Later' : 'Now'
      return (
        <div className="p-6 max-w-2xl">
          <div className="flex items-center gap-2 mb-1">
            {ws && <ws.icon className="w-4 h-4" style={{ color: ws.color }} />}
            <span className="text-sm text-muted-foreground">{ws?.name}</span>
            <ChevronRight className="w-3 h-3 text-muted-foreground" />
            <LayoutGrid className="w-4 h-4 text-purple-400" />
            <h1 className="text-xl font-bold">Product Board — {view}</h1>
          </div>
          <p className="text-sm text-muted-foreground mb-4">Active bets this cycle</p>
          <div className="space-y-2">
            {[
              { title: 'Workspace-Aware Extensions', appetite: '2 weeks', progress: 15 },
              { title: 'Chat Engine Rebuild', appetite: '1 week', progress: 40 },
              { title: 'Browse Nav Redesign', appetite: '3 days', progress: 75 },
            ].map(bet => (
              <div key={bet.title} className="border border-border rounded-lg px-4 py-3 bg-card cursor-pointer hover:border-primary/30 transition-colors">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium">{bet.title}</span>
                  <span className="text-xs text-muted-foreground">{bet.appetite}</span>
                </div>
                <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                  <div className="h-full bg-primary rounded-full" style={{ width: `${bet.progress}%` }} />
                </div>
                <div className="text-xs text-muted-foreground mt-1">{bet.progress}%</div>
              </div>
            ))}
          </div>
        </div>
      )
    }

    return <div className="p-6"><h1 className="text-xl font-bold">Extension</h1></div>
  }

  // ── Global tools ──
  if (nav.kind === 'global') {
    const info: Record<string, { label: string; icon: React.ElementType }> = {
      notifications: { label: 'Notifications', icon: Bell },
      tags: { label: 'Tags', icon: Tag },
      graph: { label: 'Graph', icon: Network },
      trash: { label: 'Trash', icon: Trash2 },
    }
    const { label, icon: Icon } = info[nav.id]
    return (
      <div className="p-6">
        <div className="flex items-center gap-2 mb-1">
          <Icon className="w-5 h-5 text-muted-foreground" />
          <h1 className="text-xl font-bold">{label}</h1>
        </div>
        <p className="text-sm text-muted-foreground">Global {label.toLowerCase()} — across all active workspaces</p>
      </div>
    )
  }

  return null
}

// ─── Main prototype ───────────────────────────────────────────────────────────

export function BrowseNavTest() {
  const [sidebarMode, setSidebarMode] = useState<SidebarMode>('browse')
  const [activeNav, setActiveNav] = useState<NavItem>({ kind: 'home' })
  const [expandedWorkspaces, setExpandedWorkspaces] = useState<Set<string>>(new Set(['hop-studios']))
  const [expandedExtensions, setExpandedExtensions] = useState<Set<string>>(new Set())
  const [treeMode, setTreeMode] = useState<Set<string>>(new Set())
  const [wsPopoverOpen, setWsPopoverOpen] = useState(false)
  const [activeWsIds, setActiveWsIds] = useState<Set<string>>(new Set(['hop-studios', 'personal']))
  const [lowerPane, setLowerPane] = useState<LowerPane>(null)
  const wsPopoverRef = useRef<HTMLDivElement>(null)

  const toggleWs = (id: string) => setExpandedWorkspaces(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n })
  const toggleExt = (wsId: string, extId: string) => { const k = `${wsId}::${extId}`; setExpandedExtensions(prev => { const n = new Set(prev); n.has(k) ? n.delete(k) : n.add(k); return n }) }
  const toggleTree = (wsId: string) => setTreeMode(prev => { const n = new Set(prev); n.has(wsId) ? n.delete(wsId) : n.add(wsId); return n })
  const toggleActiveWs = (id: string) => {
    setActiveWsIds(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n })
    // Clear lower pane if its workspace was toggled off
    setLowerPane(prev => prev?.workspaceId === id ? null : prev)
  }
  const toggleLowerPane = (kind: 'docs' | 'basecamp', wsId: string) => {
    setLowerPane(prev => (prev?.kind === kind && prev.workspaceId === wsId) ? null : { kind, workspaceId: wsId })
  }

  // Close popover on outside click
  useEffect(() => {
    const h = (e: MouseEvent) => {
      if (wsPopoverRef.current && !wsPopoverRef.current.contains(e.target as Node)) setWsPopoverOpen(false)
    }
    document.addEventListener('mousedown', h)
    return () => document.removeEventListener('mousedown', h)
  }, [])

  const modeButtons: { mode: SidebarMode; icon: React.ElementType; title: string }[] = [
    { mode: 'browse', icon: FolderTree, title: 'Browse' },
    { mode: 'search', icon: Search,    title: 'Search' },
  ]

  const lowerWs = lowerPane ? WORKSPACES.find(w => w.id === lowerPane.workspaceId) ?? null : null

  return (
    <div data-testid="browse-nav-root" className="h-screen flex bg-background text-foreground overflow-hidden">

      {/* ── Icon bar ──────────────────────────────────────────────────────── */}
      <div data-testid="browse-nav-icon-bar" className="w-12 flex-none flex flex-col bg-sidebar-accent border-r border-border">

        {/* Workspace switcher — popover trigger */}
        <div ref={wsPopoverRef} className="relative">
          <button
            data-testid="browse-nav-org-switcher"
            onClick={() => setWsPopoverOpen(o => !o)}
            title="Switch workspace"
            className={cn(
              'w-12 h-12 flex items-center justify-center border-b border-border transition-colors',
              wsPopoverOpen ? 'bg-sidebar text-sidebar-foreground' : 'text-sidebar-foreground/80 hover:bg-sidebar-accent/80'
            )}
          >
            {/* Mini avatar representing active workspaces */}
            <div className="w-6 h-6 rounded bg-primary/20 flex items-center justify-center relative">
              <span className="text-[9px] font-bold text-primary">FN</span>
              {/* Active count badge */}
              <span className="absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full bg-primary text-[8px] text-primary-foreground flex items-center justify-center font-bold leading-none">
                {activeWsIds.size}
              </span>
            </div>
          </button>

          {/* Workspace switcher popover */}
          {wsPopoverOpen && (
            <div data-testid="browse-nav-org-popover" className="absolute left-full top-0 ml-1 z-50 w-56 rounded-lg border border-border bg-popover shadow-xl py-2">
              <div className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                Organizations
              </div>
              {WORKSPACES.map(ws => {
                const WsIcon = ws.icon
                const isActive = activeWsIds.has(ws.id)
                return (
                  <button
                    key={ws.id}
                    onClick={() => toggleActiveWs(ws.id)}
                    className="w-full flex items-center gap-3 px-3 py-2 hover:bg-sidebar-accent text-left transition-colors group"
                  >
                    <span className={cn('w-4 h-4 rounded border flex items-center justify-center flex-none transition-colors', isActive ? 'bg-primary border-primary' : 'border-border')}>
                      {isActive && <span className="text-primary-foreground text-[10px]">✓</span>}
                    </span>
                    <WsIcon className="w-4 h-4 flex-none" style={{ color: ws.color }} />
                    <span className="flex-1 text-sm truncate">{ws.name}</span>
                    <Settings className="w-3.5 h-3.5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                  </button>
                )
              })}
              <div className="mx-3 my-1.5 border-t border-border" />
              <button className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors">
                <Plus className="w-3.5 h-3.5" />
                Create workspace
              </button>
              <button className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors">
                <AlignJustify className="w-3.5 h-3.5" />
                Manage organizations…
              </button>
            </div>
          )}
        </div>

        {/* Mode buttons */}
        <div className="flex flex-col items-center pt-1">
          {modeButtons.map(({ mode, icon: Icon, title }) => (
            <button
              key={mode}
              onClick={() => setSidebarMode(mode)}
              title={title}
              className={cn(
                'w-12 h-12 flex items-center justify-center transition-colors',
                sidebarMode === mode
                  ? 'bg-sidebar text-sidebar-foreground border-l-2 border-l-primary'
                  : 'text-sidebar-foreground/60 hover:bg-sidebar-accent/80 hover:text-sidebar-foreground'
              )}
            >
              <Icon className="w-4 h-4" />
            </button>
          ))}
        </div>

        <div className="flex-1" />

        <button data-testid="browse-nav-settings-btn" title="Settings" className="w-12 h-12 flex items-center justify-center text-sidebar-foreground/40 hover:text-sidebar-foreground transition-colors border-t border-border">
          <Settings className="w-4 h-4" />
        </button>
      </div>

      {/* ── Sidebar panel (switches by mode) ────────────────────────────── */}
      <div data-testid="browse-nav-sidebar" className="w-60 flex-none bg-sidebar border-r border-border overflow-hidden flex flex-col">

        {/* ── Global Open Tabs — above everything, always visible ── */}
        <GlobalOpenTabsSection activeWsIds={activeWsIds} />

        {/* ── Panel content — fills remaining space ── */}
        <div className="flex-1 overflow-hidden min-h-0">
          {sidebarMode === 'browse' ? (
            lowerPane && lowerWs ? (
              // Split mode: BrowsePanel upper + lower pane (docs or basecamp)
              <PanelGroup data-testid="browse-nav-panel-group" direction="vertical" className="h-full">
                <Panel defaultSize={55} minSize={30} className="overflow-hidden">
                  <BrowsePanel
                    activeNav={activeNav}
                    expandedWorkspaces={expandedWorkspaces}
                    expandedExtensions={expandedExtensions}
                    treeMode={treeMode}
                    lowerPane={lowerPane}
                    activeWsIds={activeWsIds}
                    onNavigate={setActiveNav}
                    onToggleWorkspace={toggleWs}
                    onToggleExtension={toggleExt}
                    onToggleTreeMode={toggleTree}
                    onToggleLowerPane={toggleLowerPane}
                  />
                </Panel>
                <PanelResizeHandle
                  className="h-1.5 flex-shrink-0 flex items-center justify-center group transition-colors hover:bg-sidebar-accent/40"
                  style={{ borderTop: `1px solid ${lowerWs.color}40` }}
                >
                  <div className="w-8 h-0.5 rounded bg-muted-foreground/20 group-hover:bg-muted-foreground/40 transition-colors" />
                </PanelResizeHandle>
                <Panel data-testid="browse-nav-lower-panel" defaultSize={45} minSize={20} className="overflow-hidden">
                  {lowerPane.kind === 'docs' ? (
                    <WorkspaceDocsPane
                      workspace={lowerWs}
                      onClose={() => setLowerPane(null)}
                      onOpen={node => setActiveNav({ kind: 'files', workspaceId: lowerPane.workspaceId })}
                    />
                  ) : (
                    <BasecampPane
                      workspace={lowerWs}
                      onClose={() => setLowerPane(null)}
                    />
                  )}
                </Panel>
              </PanelGroup>
            ) : (
              // No lower pane — BrowsePanel fills the sidebar
              <BrowsePanel
                activeNav={activeNav}
                expandedWorkspaces={expandedWorkspaces}
                expandedExtensions={expandedExtensions}
                treeMode={treeMode}
                lowerPane={lowerPane}
                activeWsIds={activeWsIds}
                onNavigate={setActiveNav}
                onToggleWorkspace={toggleWs}
                onToggleExtension={toggleExt}
                onToggleTreeMode={toggleTree}
                onToggleLowerPane={toggleLowerPane}
              />
            )
          ) : (
            <SearchPanel />
          )}
        </div>
      </div>

      {/* ── Content area ─────────────────────────────────────────────────── */}
      <div data-testid="browse-nav-content-area" className="flex-1 overflow-y-auto">
        {sidebarMode === 'browse'
          ? <ContentPanel nav={activeNav} treeMode={treeMode} onToggleTreeMode={toggleTree} />
          : (
            <div className="p-6 max-w-2xl">
              <h1 className="text-xl font-bold mb-1">Search</h1>
              <p className="text-sm text-muted-foreground">Type in the search panel on the left to find documents across all active workspaces.</p>
            </div>
          )
        }
      </div>
    </div>
  )
}
